import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import type { AssetInventoryExecutiveAsset } from '../../services/enterpriseOperationalUxApi'
import { getStandardDisplayName, normalizeComplianceStandardCode } from '../../utils/complianceStandard'
import { normalizeAssetType, assetTypeI18nKey } from '../../constants/assetTaxonomy'
import { formatRelativeTime } from '../../utils/operationalUx'
import { formatScanTimestamp } from '../../utils/scanTime'
import AssetInventoryOperationalIndicators from './AssetInventoryOperationalIndicators'
import styles from '../../styles/components/assets/AssetsInventoryIntelligence.module.scss'

type Props = {
  asset: AssetInventoryExecutiveAsset
  selected?: boolean
  onSelect: () => void
  onOpenGraph?: () => void
  onEdit?: () => void
  onDelete?: () => void
  onOpenSystem?: (systemId: string) => void
}

const LIFECYCLE_LABELS: Record<string, string> = {
  NEW: 'NEW',
  ACKNOWLEDGED: 'TRIAGED',
  IN_PROGRESS: 'IN_PROGRESS',
  MITIGATED: 'MITIGATED',
  ACCEPTED: 'ACCEPTED',
  EXCEPTION_GRANTED: 'EXCEPTION_GRANTED',
}

export default function AssetInventoryExecutiveRow({
  asset,
  selected,
  onSelect,
  onOpenGraph,
  onEdit,
  onDelete,
  onOpenSystem,
}: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const typeLabel = t(assetTypeI18nKey(normalizeAssetType(asset.type)), asset.type)
  const targetLine = (asset.networkTarget || '').trim()
  const degraded =
    (asset.pressure.degradedControls || 0) > 0 ||
    asset.assurance.controlHealth === 'DEGRADED' ||
    asset.assurance.controlHealth === 'FAILED'

  const rowClass = [
    styles.aiExecutiveRowDetails,
    selected ? styles.aiExecutiveRowSelected : '',
    degraded ? styles.aiExecutiveRowDegraded : '',
    asset.exposure.exposureLevel === 'CRITICAL' ? styles.aiExecutiveRowCritical : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <details className={rowClass}>
      <summary
        className={styles.aiExecutiveRowSummary}
        onClick={() => onSelect()}
      >
        <div className={styles.aiExecutiveHeader}>
          <div className={styles.aiExecutiveSummaryMain}>
            <span className={styles.aiExecutiveExpandTitle}>{asset.name}</span>
            {targetLine ? (
              <p className={styles.aiExecutiveTarget}>{targetLine}</p>
            ) : null}
            <p className={styles.aiExecutiveMeta}>
              {typeLabel}
              {asset.environment ? ` · ${asset.environment}` : ''}
              {asset.criticality ? ` · ${asset.criticality}` : ''}
              {' · '}
              {t('enterpriseOperationalUx.assetInventory.currentFindings', 'Current findings')}: {asset.vulnerability.count}
              {' · '}
              {asset.assurance.assuranceLevel}
              {' · '}
              {t('enterpriseOperationalUx.assetInventory.operationalExposure', 'Exposure')}:{' '}
              {asset.exposure.exposureLevel || '—'}
              {' · '}
              {t('scan.last_scan', 'Last scan')}: {formatScanTimestamp(asset.lastScan)}
            </p>
            {asset.ownerLabel ? (
              <p className={styles.aiExecutiveOwner}>
                {t('assets.add.owner', 'Owner')}: {asset.ownerLabel}
              </p>
            ) : null}
          </div>
          <AssetInventoryOperationalIndicators
            compact
            exposureLevel={asset.exposure.exposureLevel}
            internetFacing={asset.exposure.internetFacing}
            degraded={degraded}
            staleEvidence={asset.pressure.staleEvidenceCount > 0}
            runtimeWarning={Boolean(asset.runtime.latestEvent)}
            assuranceLevel={asset.assurance.assuranceLevel}
          />
        </div>
        <span className={styles.aiExecutiveExpandHint}>
          {t('assets.inventory.expandAssetDetails', 'Show asset operational details')}
        </span>
      </summary>

      <div className={styles.aiExecutiveRowBody}>
        {(asset.linkedInformationSystems?.length ?? 0) > 0 ? (
          <p className={styles.aiLinkedSystemsLine}>
            {t('assets.assetPassport.linkedSystems', 'Information systems')}:{' '}
            {asset.linkedInformationSystems!.map((sys, idx) => (
              <span key={sys.informationSystemId}>
                {idx > 0 ? ' · ' : null}
                {onOpenSystem ? (
                  <button
                    type="button"
                    className={styles.aiLinkedSystemLink}
                    onClick={() => onOpenSystem(sys.informationSystemId)}
                  >
                    {sys.name}
                  </button>
                ) : (
                  sys.name
                )}
              </span>
            ))}
          </p>
        ) : asset.linkedSystemsCount > 0 ? (
          <p className={styles.aiLinkedSystemsLine}>
            {t('assets.assetPassport.linkedSystemsCount', 'Linked to {{count}} information system(s)', {
              count: asset.linkedSystemsCount,
            })}
          </p>
        ) : null}

        {asset.complianceApplicability.standardsAffected.length ? (
          <p className={styles.aiStandardsLine}>
            {asset.complianceApplicability.standardsAffected
              .slice(0, 5)
              .map((c) => getStandardDisplayName(t, c))
              .join(' · ')}
          </p>
        ) : null}

        {asset.storytelling ? <p className={styles.aiStoryLine}>{asset.storytelling}</p> : null}

        <div className={styles.aiMetricsRow}>
          <span>
            {t('enterpriseOperationalUx.assetInventory.currentFindings', 'Current findings')}: {asset.vulnerability.count}
          </span>
          {asset.vulnerability.severity ? (
            <span>
              C:{asset.vulnerability.severity.critical ?? 0} H:{asset.vulnerability.severity.high ?? 0}{' '}
              M:{asset.vulnerability.severity.medium ?? 0} L:{asset.vulnerability.severity.low ?? 0}{' '}
              I:{asset.vulnerability.severity.info ?? 0}
            </span>
          ) : (
            <span>
              {t('assets.assetPassport.vulnerabilities', 'Vulnerabilities')}: {asset.vulnerability.criticalCount}/
              {asset.vulnerability.count}
            </span>
          )}
          <span>
            {t('enterpriseOperationalUx.assetInventory.assuranceCoverage', 'Assurance')}:{' '}
            {asset.assurance.assuranceLevel}
          </span>
          {asset.blastRadius.contribution > 0 ? (
            <span>
              {t('enterpriseOperationalUx.assetInventory.blastRadiusContribution', 'Blast radius')}:{' '}
              {asset.blastRadius.contribution}
            </span>
          ) : null}
        </div>

        {asset.vulnerability.topVulnerabilities.length ? (
          <ul className={styles.aiVulnList}>
            {asset.vulnerability.topVulnerabilities.slice(0, 3).map((v) => (
              <li key={v.id}>
                {v.cve || v.title}
                {v.lifecycleState ? (
                  <span className={styles.aiLifecycleBadge}>
                    {t(
                      `enterpriseOperationalUx.assetInventory.exposureLifecycle.${LIFECYCLE_LABELS[v.lifecycleState] || v.lifecycleState}`,
                      v.lifecycleState,
                    )}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}

        {asset.runtime.latestEvent ? (
          <p className={styles.aiRuntimeLine}>
            <span className={styles.aiRuntimeDot} aria-hidden />
            {asset.runtime.latestEvent.eventType} — {formatRelativeTime(asset.runtime.latestEvent.at)}
          </p>
        ) : null}

        {asset.workflow.stages.length ? (
          <div className={styles.aiWorkflowRow}>
            {asset.workflow.stages.slice(0, 4).map((w) => (
              <button
                key={`${w.standardCode}-${w.workflowStage}`}
                type="button"
                className={styles.aiWorkflowChip}
                onClick={() =>
                  navigate(`/compliance-workflow/${encodeURIComponent(normalizeComplianceStandardCode(w.standardCode))}`)
                }
              >
                {getStandardDisplayName(t, w.standardCode)}: {w.workflowStage}
              </button>
            ))}
          </div>
        ) : null}

        <div className={styles.aiRowActions}>
          {onEdit ? (
            <button type="button" className={styles.aiActionBtn} onClick={onEdit}>
              {t('common.edit', 'Edit')}
            </button>
          ) : null}
          {onDelete ? (
            <button type="button" className={`${styles.aiActionBtn} ${styles.aiActionBtnDanger}`} onClick={onDelete}>
              {t('common.delete', 'Delete')}
            </button>
          ) : null}
          {onOpenGraph ? (
            <button type="button" className={styles.aiActionBtn} onClick={onOpenGraph}>
              {t('assets.graph.openGraph', 'Graph')}
            </button>
          ) : null}
        </div>
      </div>
    </details>
  )
}
